import ActionTypes from "../constants/ActionTypes";
import BASE_URL from "../constants/Api";
import { authHeader } from "../helpers/auth-header";
import axiosInstance from "../helpers/refreshTokenMiddleware";

/* =========================================================
 * アクセストークン関連処理
 * ========================================================= */
export const accessTokenRequest = () => ({
  type: ActionTypes.ACCESS_TOKEN_REQUEST
});

export const accessTokenSuccess = (data) => {
  if (data.access_token) {
    localStorage.setItem("accessToken", data.access_token);
    localStorage.setItem("refreshToken", data.refresh_token);
  }

  if (data.session_id) {
    localStorage.setItem("session_id", data.session_id);
  }

  return {
    type: ActionTypes.ACCESS_TOKEN_SUCCESS,
    payload: data
  };
};

export const accessTokenFailure = (error) => ({
  type: ActionTypes.ACCESS_TOKEN_FAILURE,
  payload: error,
  error: true
});

/**
 * ログインID・パスワードを送信して、OTPトークンを取得する。
 * 正常時：OTPトークンをReduxに保存
 * 異常時：エラーメッセージを表示
 */
export const fetchAccessToken = (credentials) => async (dispatch) => {
  dispatch(accessTokenRequest());

  try {
    const response = await fetch(`${BASE_URL}?path=access-token`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok || data.status !== 200) {
      dispatch(accessTokenFailure(data.message || data.respons?.message || "エラーが発生しました。"));
      return;
    }

    const respons = data.respons;

    const {
      access_token,
      refresh_token,
      otp_enable,
      session_id,
      admin_name,
      login_id,
      authority_class
    } = respons;

    if (otp_enable === 1) {

      localStorage.setItem("login_id", credentials.login_id);
      localStorage.setItem("password", credentials.password);
      localStorage.setItem("session_id", session_id);

      dispatch(accessTokenSuccess({ otp_enable, session_id }));
      return;
    }

    dispatch(
      accessTokenSuccess({
        access_token,
        refresh_token,
        otp_enable,
        admin_name,
        login_id,
        authority_class
      })
    );

  } catch (error) {
    dispatch(accessTokenFailure(error.message));
    throw error;
  }
};


/* =========================================================
 * OTP認証関連処理
 * ========================================================= */
export const verifyOtpRequest = () => ({
  type: ActionTypes.VERIFY_OTP_REQUEST,
});

export const verifyOtpSuccess = ( data) => {
  // アクセストークンとリフレッシュトークンを保存
  if (data.access_token) {
    localStorage.setItem("accessToken", data.access_token);
    localStorage.setItem("refreshToken", data.refresh_token);
  }
  return {
    type: ActionTypes.VERIFY_OTP_SUCCESS,
    payload: data
  };
};

export const verifyOtpFailure = (error) => ({
  type: ActionTypes.VERIFY_OTP_FAILURE,
  payload: error,
  error: true,
});

/**
 * OTPコードを送信して、アクセストークンを取得する。
 */
export const verifyOtp = (otpCode) => async (dispatch) => {
  dispatch(verifyOtpRequest());

  try {
    const login_id = localStorage.getItem("login_id");
    const password = localStorage.getItem("password");
    const session_id = localStorage.getItem("session_id");

    const response = await fetch(`${BASE_URL}?path=otp-token`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({
        login_id,
        password,
        otp: otpCode,
        session_id
      })
    });

    const data = await response.json();

    if (!response.ok || data.status !== 200) {
      const error = {
        error_type: data.error_type || data.respons?.error_type || null,
        message: data.message || data.respons?.message || "認証に失敗しました。"
      };

      dispatch(verifyOtpFailure(error));
      throw new Error(error.message);
    }

    const respons = data.respons;
    dispatch(verifyOtpSuccess(respons));

    // cleanup
    localStorage.removeItem("password");
    localStorage.removeItem("session_id");

  } catch (error) {
    dispatch(verifyOtpFailure(error.message));
    throw error;
  }
};

/* =========================================================
 * OTP再送処理
 * ========================================================= */
export const resendOtpRequest = () => ({
  type: ActionTypes.RESEND_OTP_REQUEST
});

export const resendOtpSuccess = (session_id) => {
  // 新しいOTPトークンをローカルストレージに保存
  localStorage.setItem("session_id", session_id);
  return {
    type: ActionTypes.RESEND_OTP_SUCCESS,
    payload: session_id
  };
};

export const resendOtpFailure = (error) => ({
  type: ActionTypes.RESEND_OTP_FAILURE,
  payload: error
});

export const resendOtp = () => async (dispatch) => {
  dispatch(resendOtpRequest());

  try {
    const login_id = localStorage.getItem("login_id");
    const password = localStorage.getItem("password");
    const session_id = localStorage.getItem("session_id");

    const response = await fetch(`${BASE_URL}?path=access-token`, {
      method: "POST",
      headers: authHeader(),
      body: JSON.stringify({
        login_id,
        password,
        session_id
      })
    });

    const data = await response.json();

    if (!response.ok || data?.status !== 200) {
      const msg =
        data?.respons?.message ||
        data?.message ||
        "認証コードの再送信に失敗しました。";
      throw new Error(msg);
    }

    const respons = data.respons;
    if (respons.session_id) {
      localStorage.setItem("session_id", respons.session_id);
      dispatch(resendOtpSuccess(respons.session_id));
    }

  } catch (err) {
    dispatch(resendOtpFailure(err.message));
    throw err;
  }
};

/* =========================================================
 * トークンリフレッシュ処理
 * ========================================================= */
export const refreshAccessTokenRequest = () => ({
  type: ActionTypes.REFRESH_ACCESS_TOKEN_REQUEST
});

export const refreshAccessTokenSuccess = (token) => ({
  type: ActionTypes.REFRESH_ACCESS_TOKEN_SUCCESS,
  payload: token
});

export const refreshAccessTokenFailure = (error) => ({
  type: ActionTypes.REFRESH_ACCESS_TOKEN_FAILURE,
  payload: error,
  error: true
});

export const refreshAccessToken = (refreshToken) => async (dispatch) => {
  dispatch(refreshAccessTokenRequest());
  try {
    const response = await fetch(BASE_URL + '?path=refresh-token', {
      method: 'POST',
      headers: authHeader(),
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    const data = await response.json();
    if (data.status !== 200 || !response.ok) {
      dispatch(refreshAccessTokenFailure(data.respons?.message || "エラーが発生しました。"));
      return;
    }

    const newAccessToken = data.newAccessToken;
    dispatch(refreshAccessTokenSuccess(newAccessToken));
    return newAccessToken;
  } catch (error) {
    dispatch(refreshAccessTokenFailure(error.message));
    throw error; 
  }
};

/* =========================================================
 * ユーザー情報取得処理
 * ========================================================= */
export const fetchUserRequest = () => ({
  type: ActionTypes.FETCH_USER_REQUEST,
});

export const fetchUserSuccess = (user) => ({
  type: ActionTypes.FETCH_USER_SUCCESS,
  payload: user.respons
});

export const fetchUserFailure = (error) => ({
  type: ActionTypes.FETCH_USER_FAILURE,
  payload: error,
  error: true
});

export const fetchUserInfo = () => async (dispatch) => {
  dispatch(fetchUserRequest());

  try {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      window.location.href = '/login';
      return;
    }

    const response = await axiosInstance.get(BASE_URL + '?path=admin&type=get-login-info', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    dispatch(fetchUserSuccess(response.data));

  } catch (error) {
    dispatch(fetchUserFailure(error.message));
    throw error;
  }
};

/* =========================================================
 * ログアウト処理
 * ========================================================= */
export const logoutRequest = () => ({
  type: ActionTypes.LOGOUT_REQUEST
});

export const logoutSuccess = () => ({
  type: ActionTypes.LOGOUT_SUCCESS
});

export const logoutFailure = (error) => ({
  type: ActionTypes.LOGOUT_FAILURE,
  payload: error,
  error: true
});

export const logoutUser = () => async (dispatch) => {
  dispatch(logoutRequest());

  const remember = localStorage.getItem("remember") === "true";
  const savedLoginId = localStorage.getItem("login_id");
  const refreshToken = localStorage.getItem("refreshToken");

  try {

    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      window.location.href = '/login';
      return;
    }

    await axiosInstance.post(
      `${BASE_URL}?path=logout`,
      {
        refresh_token: refreshToken,
        logout_all: false
      },
      {
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        }
      }
    );

    dispatch(logoutSuccess());

  } catch (error) {
    console.warn("Logout API failed:", error?.response?.status, error?.message);
    dispatch(logoutFailure(error.message));
  }

  localStorage.clear();

  // 「ログインIDを記憶する」が有効な場合は再保存
  if (remember) {
    localStorage.setItem("remember", "true");
    if (savedLoginId) {
      localStorage.setItem("login_id", savedLoginId);
    }
  }

  // 明示的に削除
  localStorage.setItem("lastAction", "logout");
  window.location.href = "/login";
};